import React from 'react'
import * as lodash from 'lodash'
import clsx from 'clsx'
import { Logger } from 'wdc-cube'
import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'

import AddIcon from '@mui/icons-material/Add'
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded'
import CropIcon from '@mui/icons-material/Crop'
import DeleteSweep from '@mui/icons-material/DeleteSweep'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import RemoveIcon from '@mui/icons-material/Remove'
import RouteIcon from '@mui/icons-material/Route'
import TripOriginIcon from '@mui/icons-material/TripOrigin'
import Tooltip from '@mui/material/Tooltip'

import * as L from 'leaflet'
import { MapContainer, TileLayer } from 'react-leaflet'

import {
    CardIconButton,
    LifesHubCard,
    LifesHubCardBody,
    LifesHubCardHeader,
    MapSlider,
    MapToolbarGap
} from 'src/utils/views'
import { FCClassContext, classToFComponent } from 'src/utils/views'
import { MapCardScope, SliderScope } from '../ta_scopes'

const LOG = Logger.get('TA-VIEW-MAP-CARD')

const MapToolbar = styled('div')({
    position: 'absolute',
    top: 10,
    left: 10,
    zIndex: 1100,
    boxShadow: 'var(--shadow-1)',
    backgroundClip: 'padding-box',
    background: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    borderRadius: 9
})

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        alignSelf: 'stretch',
        flexGrow: 1,
        minHeight: 0
    },
    mapHolder: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0,
        borderRadius: 'var(--radius)',
        border: '1px solid var(--border)',
        overflow: 'hidden'
    },
    map: {
        display: 'flex',
        flexGrow: 1
    },
    toolButton: {
        width: 34,
        height: 34,
        padding: 5,
        cursor: 'pointer',
        color: '#2d332f',
        backgroundColor: 'var(--surface)',
        borderLeft: '1px solid var(--border-strong)',
        borderRight: '1px solid var(--border-strong)',
        '&:hover': {
            backgroundColor: '#edf1ee'
        }
    },
    toolButtonActive: {
        color: 'var(--green-800)',
        backgroundColor: 'var(--green-050)'
    },
    topToolButton: {
        borderTop: '1px solid var(--border-strong)',
        borderTopLeftRadius: 9,
        borderTopRightRadius: 9
    },
    bottomToolButton: {
        borderBottom: '1px solid var(--border-strong)',
        borderBottomLeftRadius: 9,
        borderBottomRightRadius: 9
    }
})

export type MapCardViewProps = {
    className?: string
    scope: MapCardScope
}

class MapCardViewClass implements FCClassContext<MapCardViewProps> {
    // :: Fields
    scope!: MapCardScope
    mapRef: L.Map | null = null
    mapResizeObserver = new ResizeObserver(() => this.onMapResize())
    holderEl: HTMLDivElement | null = null

    // :: Emissors

    readonly emitOnHolderAttached = (holderEl: HTMLDivElement | null) => {
        this.holderEl && this.mapResizeObserver.unobserve(this.holderEl)
        this.holderEl = holderEl
        this.holderEl && this.mapResizeObserver.observe(this.holderEl)
    }

    readonly emitExpandClick = () => this.scope.onExpandClick().catch(LOG.caught)
    readonly emitShrunkClick = () => this.scope.onShrunkClick().catch(LOG.caught)
    readonly emitClearFilters = () => this.scope.onClearFilters().catch(LOG.caught)
    readonly emitZoomIn = () => (this.mapRef && this.mapRef.zoomIn(1), void 0)
    readonly emitZoomOut = () => (this.mapRef && this.mapRef.zoomOut(1), void 0)
    readonly emitSetRouteMode = () => this.scope.onSetRouteMode().catch(LOG.caught)
    readonly emitSetAreaMode = () => this.scope.onSetAreaMode().catch(LOG.caught)
    readonly emitSetCircleMode = () => this.scope.onSetCircleMode().catch(LOG.caught)

    readonly emitMapChanged = (value: L.Map | null) => {
        if (this.mapRef !== value) {
            this.mapRef = value
            this.scope.onMapChanged(value).catch(LOG.caught)
        }
    }

    // :: Methods

    onDetach() {
        this.holderEl && this.mapResizeObserver.unobserve(this.holderEl)
        this.mapResizeObserver.disconnect()
    }

    onSyncState({ scope }: MapCardViewProps) {
        this.scope = scope
    }

    onMapResize() {
        this.mapRef && this.mapRef.invalidateSize()
    }

    render({ className, scope }: MapCardViewProps) {
        const classes = useStyles().classes

        const mode = (active: boolean) => clsx(classes.toolButton, active && classes.toolButtonActive)
        const topToolButton = clsx(classes.toolButton, classes.topToolButton)
        const bottomToolButton = clsx(classes.toolButton, classes.bottomToolButton)
        const singleToolButton = clsx(classes.toolButton, classes.topToolButton, classes.bottomToolButton)

        return (
            <LifesHubCard className={clsx(classes.view, className)} elevation={0}>
                <LifesHubCardHeader
                    title="Mapa de atuação"
                    actions={
                        <Tooltip title={scope.expanded ? 'Restaurar mapa' : 'Maximizar mapa'}>
                            <CardIconButton
                                aria-label={scope.expanded ? 'Restaurar mapa' : 'Maximizar mapa'}
                                onClick={scope.expanded ? this.emitShrunkClick : this.emitExpandClick}
                            >
                                {scope.expanded && <CloseFullscreenRoundedIcon />}
                                {!scope.expanded && <LaunchRoundedIcon />}
                            </CardIconButton>
                        </Tooltip>
                    }
                />
                <LifesHubCardBody>
                    <div ref={this.emitOnHolderAttached} className={classes.mapHolder}>
                        <MapToolbar>
                            <Tooltip title="Aproximar">
                                <AddIcon className={topToolButton} aria-label="Aproximar" onClick={this.emitZoomIn} />
                            </Tooltip>
                            <Tooltip title="Afastar">
                                <RemoveIcon
                                    className={bottomToolButton}
                                    aria-label="Afastar"
                                    onClick={this.emitZoomOut}
                                />
                            </Tooltip>
                            <MapToolbarGap />
                            <Tooltip title="Selecionar por rota">
                                <RouteIcon
                                    className={clsx(mode(scope.mode === 'route'), classes.topToolButton)}
                                    aria-label="Selecionar por rota"
                                    onClick={this.emitSetRouteMode}
                                />
                            </Tooltip>
                            <Tooltip title="Selecionar por área">
                                <CropIcon
                                    className={mode(scope.mode === 'polygon')}
                                    aria-label="Selecionar por área"
                                    onClick={this.emitSetAreaMode}
                                />
                            </Tooltip>
                            <Tooltip title="Selecionar por círculo">
                                <TripOriginIcon
                                    className={clsx(mode(scope.mode === 'circle'), classes.bottomToolButton)}
                                    aria-label="Selecionar por círculo"
                                    onClick={this.emitSetCircleMode}
                                />
                            </Tooltip>
                            {scope.showClearButton && (
                                <>
                                    <MapToolbarGap />
                                    <Tooltip title="Limpar filtros aplicados ao mapa">
                                        <DeleteSweep
                                            className={singleToolButton}
                                            aria-label="Limpar filtros aplicados ao mapa"
                                            onClick={this.emitClearFilters}
                                        />
                                    </Tooltip>
                                </>
                            )}
                        </MapToolbar>
                        <MapContainer
                            className={classes.map}
                            zoomControl={false}
                            scrollWheelZoom={true}
                            ref={this.emitMapChanged}
                        >
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        </MapContainer>
                        {scope.slider.visible && <CustomSlider scope={scope.slider} />}
                    </div>
                </LifesHubCardBody>
            </LifesHubCard>
        )
    }
}
export const MapCardView = classToFComponent(MapCardViewClass, React)
export default MapCardView

// :: Internal

export type CustomSliderProps = {
    className?: string
    style?: React.CSSProperties
    scope: SliderScope
}

const CustomSlider = classToFComponent(
    class CustomSliderClass implements FCClassContext<CustomSliderProps> {
        scope!: SliderScope

        render({ className, style, scope }: CustomSliderProps): React.JSX.Element {
            this.scope = scope
            return (
                <MapSlider
                    className={className}
                    style={style}
                    size="small"
                    aria-label="Raio de seleção"
                    valueLabelDisplay="auto"
                    value={scope.value}
                    min={scope.min}
                    max={scope.max}
                    step={scope.step}
                    valueLabelFormat={this.formatSliderLabel}
                    onChange={this.emitKmSliderChange}
                />
            )
        }

        readonly emitKmSliderChange = (_event: Event, value: number | number[]) => {
            if (lodash.isNumber(value)) {
                this.scope.onValueChanged(value)
            }
        }

        readonly formatSliderLabel = (value: number) => {
            return `${value}${this.scope.unit}`
        }
    },
    React
)
